import { API_BASE_URL, httpRequest } from './http.js'

/**
 * Annonces de l'infoservice : une image fixe par annonce, diffusée les jours
 * cochés. Miroir de `routes/infoservice-announcements.routes.ts`.
 */

export interface AnnouncementDayPlan {
  day: string
  scheduledIds: string[]
  onAirIds: string[]
  priorityIds: string[]
  missingImageIds: string[]
  loopSec: number
}

export interface Announcement {
  id: string
  channelId: string
  advertiserId: string | null
  advertiserName: string | null
  /** Adhérent : rattaché d'office par l'organisme, modifiable dans la fiche. */
  memberId: string | null
  memberName: string | null
  organizationLabel: string
  eventLabel: string
  imagePath: string | null
  imageFileName: string | null
  imageWidth: number | null
  imageHeight: number | null
  imageAvailable: boolean
  hasText: boolean
  priority: boolean
  displayDurationSec: number | null
  cancelled: boolean
  notes: string | null
  archivedAt: string | null
  importedAt: string | null
  /** Tenue par la feuille Google : noms, « Texte », jours et annulation s'y modifient. */
  sheetLinked: boolean
  /** Ligne de la feuille à la dernière synchronisation (`null` : disparue). */
  sheetRow: number | null
  createdAt: string
  updatedAt: string
  days: string[]
  firstDay: string | null
  lastDay: string | null
  totalDays: number
  /** `fromSheet` : écrit dans une case de la feuille, remplacé à chaque synchronisation. */
  dayNotes: Array<{ id: string; day: string; text: string; fromSheet: boolean }>
}

export interface AnnouncementList {
  announcements: Announcement[]
  dayPlan: AnnouncementDayPlan
}

export type AnnouncementPatch = Partial<{
  advertiserId: string | null
  /** `null` : l'adhérent de l'organisme, rattaché d'office. */
  memberId: string | null
  organizationLabel: string
  eventLabel: string
  hasText: boolean
  priority: boolean
  displayDurationSec: number | null
  cancelled: boolean
  notes: string | null
  archived: boolean
}>

/** Copies réduites servies à l'écran : 160, 480 et 960 px de large. */
export type AnnouncementImageSize = 'thumb' | 'card' | 'large'

export interface ImageFileEntry {
  path: string
  fileName: string
  sizeBytes: number
  modifiedAt: string
  usedBy: Array<{ id: string; label: string }>
}

export interface ImportPreviewRow {
  sheetRow: number
  rawName: string
  organizationLabel: string
  eventLabel: string
  hasText: boolean
  cancelled: boolean
  days: string[]
  dayNotes: Array<{ day: string; text: string }>
  notes: string | null
  warnings: string[]
  advertiserId: string | null
  advertiserName: string | null
  archived: boolean
}

export interface ImportPreview {
  sheetName: string
  firstYear: number
  checkedFill: string | null
  firstDay: string | null
  lastDay: string | null
  rows: ImportPreviewRow[]
  skippedRows: Array<{ sheetRow: number; reason: string }>
  counts: { total: number; active: number; archived: number; days: number; dayNotes: number; advertisersMatched: number }
  alreadyImported: number
}

export interface SheetSyncSummary {
  rows: number
  created: number
  updated: number
  renamed: number
  adopted: number
  archived: number
  unchanged: number
  createdNames: string[]
  renamedNames: Array<{ from: string; to: string }>
  archivedNames: string[]
  warnings: Array<{ sheetRow: number; name: string; message: string }>
}

export interface SheetSyncState {
  connected: boolean
  sourceUrl: string | null
  spreadsheetId: string | null
  editUrl: string | null
  sheetName: string | null
  enabled: boolean
  intervalMinutes: number
  rowCount: number
  checkedFill: string | null
  lastAttemptAt: string | null
  lastSuccessAt: string | null
  lastStatus: 'OK' | 'REFUSED' | 'ERROR' | null
  lastMessage: string | null
  lastSummary: SheetSyncSummary | null
}

export interface SheetSyncResult {
  status: 'OK' | 'REFUSED' | 'SKIPPED'
  message: string | null
  summary: SheetSyncSummary | null
  applied: boolean
}

export interface SheetTabInfo {
  sheetName: string
  usable: boolean
  rows: number
  checkedFill: string | null
  firstDay: string | null
  lastDay: string | null
  problem: string | null
}

export interface SheetInspection {
  spreadsheetId: string
  editUrl: string
  tabs: SheetTabInfo[]
  suggested: string | null
}

/**
 * Adhérent : l'organisme d'une ou plusieurs annonces, repris du fichier du
 * club, avec son droit de semaines par année d'adhésion.
 */
export interface MemberSummary {
  id: string
  channelId: string
  name: string
  /** Début de l'année d'adhésion (adhésion ou dernier renouvellement). */
  membershipDate: string | null
  /** Année d'adhésion (dernier jour compris), `null` sans date. */
  period: { start: string; end: string } | null
  expired: boolean
  /** Droit annuel : semaines d'annonces par année d'adhésion. */
  weekQuota: number
  /** Catégorie du club, 1 à 9. */
  category: number | null
  fee: string | null
  followedBy: string | null
  reminder: string | null
  newsletter: boolean | null
  notes: string | null
  importedAt: string | null
  announcementCount: number
  weeksUsed: number
  weeksPassed: number
  weeksPlanned: number
  quota: number
  weeksOver: number
}

export interface MemberAnnouncement {
  id: string
  organizationLabel: string
  eventLabel: string
  cancelled: boolean
  archived: boolean
  days: number
  weeks: number
  firstDay: string | null
  lastDay: string | null
  outsideDays: number
}

export interface MemberDetail extends MemberSummary {
  memberYears: number[]
  contactName: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  secondaryContact: string | null
  address: string | null
  postalCode: string | null
  city: string | null
  /** Écritures de la feuille rattachées à cet adhérent. */
  spellings: string[]
  announcements: MemberAnnouncement[]
}

export type MemberPatch = Partial<{
  name: string
  membershipDate: string | null
  weekQuota: number
  category: number | null
  fee: string | null
  followedBy: string | null
  reminder: string | null
  newsletter: boolean | null
  memberYears: number[]
  contactName: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  secondaryContact: string | null
  address: string | null
  postalCode: string | null
  city: string | null
  notes: string | null
}>

/** Écriture d'un organisme dans la feuille, et l'adhérent qui lui revient. */
export interface Spelling {
  nameKey: string
  label: string
  announcementCount: number
  activeCount: number
  /** `CHOSEN` : choix de l'exploitant ; `EXACT` : même nom ; `IGNORED` : pas un adhérent. */
  status: 'CHOSEN' | 'EXACT' | 'IGNORED' | 'UNRESOLVED'
  memberId: string | null
  memberName: string | null
  suggestion: { memberId: string; name: string } | null
}

export type SpellingChoice = { memberId: string } | { ignored: true } | null

export interface MemberImportPreview {
  rows: Array<{
    row: number
    name: string
    membershipDate: string | null
    weekQuota: number
    category: number | null
    fileCredit: number | null
    existingName: string | null
  }>
  warnings: string[]
  created: number
  updated: number
  attachedSpellings: number
  attachedAnnouncements: number
  unresolvedSpellings: number
  unresolvedAnnouncements: number
}

export interface MemberImportResult {
  created: number
  updated: number
  attached: number
  unresolved: number
}

/** Temps d'antenne d'une journée (mode diaporama seulement). */
export interface Airtime {
  day: string
  totalMs: number
  untrackedPlages: number
  plages: Array<{ firstItemId: string; start: string; end: string; durationMs: number; ongoing: boolean; estimated: boolean; airings: number }>
  announcements: Array<{ announcementId: string; organizationLabel: string; eventLabel: string; memberName: string | null; airings: number; totalMs: number }>
}

const BASE = '/infoservice-announcements'

function query(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1]))
  return entries.length ? `?${entries.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&')}` : ''
}

function json(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) }
}

function fileForm(file: File): FormData {
  const form = new FormData()
  form.append('file', file, file.name)
  return form
}

export const announcementsApi = {
  list: (params: { channelId: string; from: string; to: string; archived?: 'active' | 'archived' | 'all'; day?: string }) =>
    httpRequest<AnnouncementList>(`${BASE}${query(params)}`, { cache: 'no-store' }),

  get: (id: string) => httpRequest<Announcement>(`${BASE}/${id}`, { cache: 'no-store' }),

  create: (data: AnnouncementPatch & { channelId: string; organizationLabel: string; days?: string[] }) =>
    httpRequest<Announcement>(BASE, json('POST', data)),

  update: (id: string, data: AnnouncementPatch) => httpRequest<Announcement>(`${BASE}/${id}`, json('PATCH', data)),

  remove: (id: string) => httpRequest<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  duplicate: (id: string) => httpRequest<Announcement>(`${BASE}/${id}/duplicate`, json('POST')),

  setDays: (id: string, change: { add?: string[]; remove?: string[]; range?: { from: string; to: string; mode?: 'add' | 'remove' } }) =>
    httpRequest<Announcement>(`${BASE}/${id}/days`, json('PUT', change)),

  addNote: (id: string, note: { day: string; text: string }) =>
    httpRequest<Announcement>(`${BASE}/${id}/day-notes`, json('POST', note)),

  removeNote: (id: string, noteId: string) =>
    httpRequest<Announcement>(`${BASE}/${id}/day-notes/${noteId}`, { method: 'DELETE' }),

  uploadImage: (id: string, file: File) =>
    httpRequest<Announcement>(`${BASE}/${id}/image`, { method: 'POST', body: fileForm(file) }),

  linkImage: (id: string, path: string) =>
    httpRequest<Announcement>(`${BASE}/${id}/image/link`, json('POST', { path })),

  detachImage: (id: string) => httpRequest<Announcement>(`${BASE}/${id}/image`, { method: 'DELETE' }),

  imageFiles: (channelId: string) =>
    httpRequest<ImageFileEntry[]>(`${BASE}/image-files${query({ channelId })}`, { cache: 'no-store' }),

  previewImport: (channelId: string, file: File) =>
    httpRequest<ImportPreview>(`${BASE}/import/preview${query({ channelId })}`, { method: 'POST', body: fileForm(file) }),

  applyImport: (channelId: string, file: File) =>
    httpRequest<{ created: number; days: number; dayNotes: number }>(`${BASE}/import/apply${query({ channelId })}`, { method: 'POST', body: fileForm(file) }),

  members: {
    list: (channelId: string) => httpRequest<MemberSummary[]>(`${BASE}/members${query({ channelId })}`, { cache: 'no-store' }),
    get: (id: string) => httpRequest<MemberDetail>(`${BASE}/members/${id}`, { cache: 'no-store' }),
    create: (data: MemberPatch & { channelId: string; name: string }) => httpRequest<MemberDetail>(`${BASE}/members`, json('POST', data)),
    update: (id: string, data: MemberPatch) => httpRequest<MemberDetail>(`${BASE}/members/${id}`, json('PATCH', data)),
    merge: (id: string, intoId: string) => httpRequest<MemberDetail>(`${BASE}/members/${id}/merge`, json('POST', { intoId })),
    remove: (id: string) => httpRequest<void>(`${BASE}/members/${id}`, { method: 'DELETE' }),
    spellings: (channelId: string) => httpRequest<Spelling[]>(`${BASE}/members/spellings${query({ channelId })}`, { cache: 'no-store' }),
    setSpelling: (channelId: string, label: string, choice: SpellingChoice) =>
      httpRequest<Spelling[]>(`${BASE}/members/spellings`, json('PUT', { channelId, label, choice })),
    previewImport: (channelId: string, file: File) =>
      httpRequest<MemberImportPreview>(`${BASE}/members/import/preview${query({ channelId })}`, { method: 'POST', body: fileForm(file) }),
    applyImport: (channelId: string, file: File) =>
      httpRequest<MemberImportResult>(`${BASE}/members/import/apply${query({ channelId })}`, { method: 'POST', body: fileForm(file) }),
  },

  airtime: (channelId: string, day: string) =>
    httpRequest<Airtime>(`${BASE}/airtime${query({ channelId, day })}`, { cache: 'no-store' }),

  sheetSync: {
    state: (channelId: string) =>
      httpRequest<SheetSyncState>(`${BASE}/sheet-sync${query({ channelId })}`, { cache: 'no-store' }),
    inspect: (channelId: string, url: string) =>
      httpRequest<SheetInspection>(`${BASE}/sheet-sync/inspect`, json('POST', { channelId, url })),
    preview: (channelId: string, url: string, sheetName: string) =>
      httpRequest<SheetSyncResult>(`${BASE}/sheet-sync/preview`, json('POST', { channelId, url, sheetName })),
    connect: (channelId: string, url: string, sheetName: string) =>
      httpRequest<{ state: SheetSyncState; result: SheetSyncResult }>(`${BASE}/sheet-sync`, json('PUT', { channelId, url, sheetName })),
    run: (channelId: string, force = false) =>
      httpRequest<{ state: SheetSyncState; result: SheetSyncResult }>(`${BASE}/sheet-sync/run`, json('POST', { channelId, force })),
    setEnabled: (channelId: string, enabled: boolean) =>
      httpRequest<SheetSyncState>(`${BASE}/sheet-sync`, json('PATCH', { channelId, enabled })),
    disconnect: (channelId: string) =>
      httpRequest<SheetSyncState>(`${BASE}/sheet-sync${query({ channelId })}`, { method: 'DELETE' }),
  },

  /**
   * URL de l'image d'une annonce ; `version` casse le cache après un
   * remplacement. `size` demande une copie réduite pour l'écran (l'antenne
   * diffuse toujours l'original) : `thumb` (tableau), `large` (fiche).
   */
  imageUrl: (id: string, version?: string, size?: AnnouncementImageSize) =>
    `${API_BASE_URL}${BASE}/${id}/image${query({ v: version, size })}`,

  /** Image du dossier INFOSERVICES ; `version` = sa date de modification. */
  imageFileUrl: (channelId: string, path: string, options: { size?: AnnouncementImageSize; version?: string } = {}) =>
    `${API_BASE_URL}${BASE}/image-files/preview${query({ channelId, path, size: options.size, v: options.version })}`,
}
